import { VStack, Button, Divider, Stack, Box, useColorModeValue } from '@chakra-ui/react';
import { createElement, type ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { IconBaseProps, IconType } from 'react-icons';
import { FiCompass, FiVideo, FiGrid, FiFilm, FiTv, FiBookOpen, FiAward, FiVideo as FiVideoIcon, FiGrid as FiGridIcon } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const quickLinks: { label: string; icon: IconType; action: (navigate: ReturnType<typeof useNavigate>) => void; requiresAuth?: boolean }[] = [
  { label: 'Browse', icon: FiCompass, action: navigate => navigate('/search') },
  { label: 'Your Videos', icon: FiVideo, action: navigate => navigate('/creator-dashboard'), requiresAuth: true },
  { label: 'Library', icon: FiGrid, action: navigate => navigate('/watchlist'), requiresAuth: true },
  { label: 'Premium', icon: FiAward, action: navigate => navigate('/subscription-management') }
];

const categoryItems: { label: string; path: string; icon: IconType }[] = [
  { label: 'All', path: '/', icon: FiCompass },
  { label: 'Movies', path: '/movies', icon: FiFilm },
  { label: 'Series', path: '/series', icon: FiTv },
  { label: 'Shorts', path: '/shorts', icon: FiVideoIcon },
  { label: 'Books', path: '/books', icon: FiBookOpen },
  { label: 'Comics', path: '/comics', icon: FiGridIcon }
];

const AppSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const sidebarBg = useColorModeValue('linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(244,240,255,0.95) 100%)', 'linear-gradient(180deg, rgba(22,22,31,0.98) 0%, rgba(16,16,24,0.98) 100%)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const categoryLabelColor = useColorModeValue('gray.500', 'gray.400');

  // Filter links based on authentication
  const visibleLinks = quickLinks.filter(link => !link.requiresAuth || user);

  return (
    <Box
      height="100%"
      overflowY="auto"
      px={0}
      py={0}
    >
      <Box
        bg={sidebarBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="0 32px 32px 0"
        px={5}
        py={6}
        boxShadow="2xl"
        height="fit-content"
        minH="100%"
      >
        <VStack align="stretch" spacing={6}>
          <VStack align="stretch" spacing={2}>
            {visibleLinks.map(link => (
              <Button
                key={link.label}
                justifyContent="flex-start"
                variant="ghost"
                colorScheme="purple"
                leftIcon={createElement(link.icon as ComponentType<IconBaseProps>, { size: 20 })}
                borderRadius="lg"
                fontWeight="semibold"
                onClick={() => link.action(navigate)}
              >
                {link.label}
              </Button>
            ))}
          </VStack>

          <Divider borderColor={borderColor} />

          <VStack align="stretch" spacing={3}>
            <Box fontSize="sm" fontWeight="semibold" color={categoryLabelColor} textTransform="uppercase" letterSpacing="wide">
              Categories
            </Box>
            <Stack spacing={2}>
              {categoryItems.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    justifyContent="flex-start"
                    variant={isActive ? 'solid' : 'ghost'}
                    colorScheme="purple"
                    borderRadius="lg"
                    leftIcon={createElement(item.icon as ComponentType<IconBaseProps>, { size: 18 })}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          </VStack>

          <Button
            size="lg"
            colorScheme="purple"
            variant="solid"
            borderRadius="lg"
            onClick={() => navigate('/upload/category')}
          >
            Upload Your Content
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default AppSidebar;

